import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ArrowUp, ArrowDown, Trash2, X } from 'lucide-react';

const QuestionEditorCard = ({ 
  question, 
  index, 
  total, 
  onUpdate, 
  onDelete, 
  onMoveUp, 
  onMoveDown,
  canEdit = true 
}) => {
  const [localQuestion, setLocalQuestion] = useState(question || {
    text: '',
    responseType: 'Yes/No',
    failureCreatesDeviation: false,
    evidenceRequired: 'None',
    aiEvidenceAssist: false,
    clauseStandard: '',
    clauseReference: ''
  });

  useEffect(() => {
    if (question) {
      setLocalQuestion(question);
    }
  }, [question]);

  const handleChange = (field, value) => {
    const updated = { ...localQuestion, [field]: value };
    setLocalQuestion(updated);
    if (onUpdate) onUpdate(updated);
  };

  const responseTypes = ['Yes/No', 'Numeric', 'Multiple choice', 'Free text'];
  const evidenceTypes = ['None', 'Photo', 'Reading', 'Attachment'];
  const standards = ['Schedule-M', 'WHO-GMP'];

  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-1 rounded">
              Q{index + 1}
            </span>
            {!canEdit && (
              <span className="text-xs text-slate-500 italic">Read-only</span>
            )}
          </div>
          {canEdit && (
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={onMoveUp}
                disabled={index === 0}
                className="h-7 w-7 p-0 text-slate-500 hover:text-slate-800"
              >
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onMoveDown}
                disabled={index === total - 1}
                className="h-7 w-7 p-0 text-slate-500 hover:text-slate-800"
              >
                <ArrowDown className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDelete}
                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700">Question Text *</Label>
            <Textarea
              value={localQuestion.text}
              onChange={(e) => handleChange('text', e.target.value)}
              disabled={!canEdit}
              className="border-slate-200 min-h-[60px]"
              placeholder="Enter question text..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Response Type *</Label>
              <Select
                value={localQuestion.responseType}
                onValueChange={(v) => handleChange('responseType', v)}
                disabled={!canEdit}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {responseTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Evidence Required</Label>
              <Select
                value={localQuestion.evidenceRequired || 'None'}
                onValueChange={(v) => handleChange('evidenceRequired', v)}
                disabled={!canEdit}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {evidenceTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200">
            <div className="space-y-0.5">
              <Label htmlFor={`deviation-${index}`} className="text-slate-700 cursor-pointer">
                Failure creates Deviation
              </Label>
              <p className="text-xs text-slate-500">Automatically create deviation on failure</p>
            </div>
            <Switch
              id={`deviation-${index}`}
              checked={localQuestion.failureCreatesDeviation || false}
              onCheckedChange={(checked) => handleChange('failureCreatesDeviation', checked)}
              disabled={!canEdit}
            />
          </div>

          {(localQuestion.evidenceRequired === 'Photo' || localQuestion.evidenceRequired === 'Reading') && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200">
              <div className="space-y-0.5">
                <Label htmlFor={`ai-${index}`} className="text-slate-700 cursor-pointer">
                  AI Evidence Assist
                </Label>
                <p className="text-xs text-slate-500">Enable AI-powered evidence validation</p>
              </div>
              <Switch
                id={`ai-${index}`}
                checked={localQuestion.aiEvidenceAssist || false}
                onCheckedChange={(checked) => handleChange('aiEvidenceAssist', checked)}
                disabled={!canEdit}
              />
            </div>
          )}

          <div className="space-y-2 pt-2 border-t border-slate-200">
            <Label className="text-slate-700">Clause Reference</Label>
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={localQuestion.clauseStandard || ''}
                onValueChange={(v) => handleChange('clauseStandard', v)}
                disabled={!canEdit}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue placeholder="Standard" />
                </SelectTrigger>
                <SelectContent>
                  {standards.map(std => (
                    <SelectItem key={std} value={std}>{std}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={localQuestion.clauseReference || ''}
                onChange={(e) => handleChange('clauseReference', e.target.value)}
                disabled={!canEdit}
                className="border-slate-200"
                placeholder="e.g., Schedule-M: Premises & Utilities"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuestionEditorCard;

